(() => {
  const ROOT_SELECTOR = '[data-cleaning-service-form]';
  const MAX_FILES = 4;
  const MAX_FILE_SIZE = 10 * 1024 * 1024;
  const MAX_TOTAL_FILE_SIZE = 15 * 1024 * 1024;
  const SEARCH_TIMEOUT = 6000;
  const SUBMIT_MODAL_MIN_VISIBLE = 700;
  const POSTAL_LOOKUP_URL = 'https://zipcloud.ibsnet.co.jp/api/search';

  const UNKNOWN_PRODUCT_NAME = '品番不明・画像添付必須';
  const UNKNOWN_CLEANING_PRICE = '¥8,800 ～ ¥11,000（税込）';

  function formatCleaningPrice(value) {
    if (value == null || (typeof value === 'string' && value.trim() === '')) {
      return UNKNOWN_CLEANING_PRICE;
    }
    const prices = { 'Cleaning 8800': '¥8,800（税込）', 'Cleaning 9900': '¥9,900（税込）', 'Cleaning 11000': '¥11,000（税込）' };
    return typeof value === 'string' && Object.prototype.hasOwnProperty.call(prices, value.trim())
      ? prices[value.trim()] : '';
  }

  function japanDateKey(now = new Date()) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit'
    }).formatToParts(now);
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return `${values.year}-${values.month}-${values.day}`;
  }

  function minimumWearDateKey(now = new Date()) {
    const date = new Date(`${japanDateKey(now)}T00:00:00Z`);
    date.setUTCDate(date.getUTCDate() + 30);
    return date.toISOString().slice(0, 10);
  }

  function isAllowedWearDate(value, now = new Date()) {
    if (!value) return true;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
    const parsed = new Date(`${value}T00:00:00Z`);
    return Number.isFinite(parsed.getTime())
      && parsed.toISOString().slice(0, 10) === value
      && value >= minimumWearDateKey(now);
  }

  async function fetchWithTimeout(url, options = {}, timeout = SEARCH_TIMEOUT) {
    const controller = new AbortController();
    const parentSignal = options.signal;
    let timedOut = false;
    const forwardAbort = () => controller.abort();

    if (parentSignal) {
      if (parentSignal.aborted) controller.abort();
      else parentSignal.addEventListener('abort', forwardAbort, { once: true });
    }

    const timer = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeout);

    try {
      return await fetch(url, { ...options, signal: controller.signal });
    } catch (error) {
      if (timedOut) throw new Error('Search request timed out');
      throw error;
    } finally {
      window.clearTimeout(timer);
      if (parentSignal) parentSignal.removeEventListener('abort', forwardAbort);
    }
  }

  function initializeCleaningForm(root) {
    if (!root || root.dataset.cleaningRequestInitialized === 'true') return;
    root.dataset.cleaningRequestInitialized = 'true';

    const shop = setupShopVariant(root);
    if (shop) return;

    const form = root.querySelector('form');
    if (!form) return;
    form.noValidate = true;

    const panels = Array.from(root.querySelectorAll('[data-step-panel]'));
    const basicStep = panels.findIndex((panel) => panel.querySelector('[data-product-query]'));
    const customerStep = panels.findIndex((panel) => panel.querySelector('[data-wear-date]'));
    const indicators = Array.from(root.querySelectorAll('[data-step-indicator]'));
    const confirmation = root.querySelector('[data-confirmation]');
    const submitStatus = root.querySelector('[data-submit-status]');
    const submitButton = root.querySelector('[data-submit-button]');
    const submitLabel = root.querySelector('[data-submit-label]');
    const confirmationAcknowledgement = root.querySelector('[data-confirmation-acknowledgement]');
    const termsOpenButton = root.querySelector('[data-terms-open]');
    const overviewChecks = Array.from(root.querySelectorAll('[data-overview-check]'));
    const overviewNext = root.querySelector('[data-overview-next]');
    const overviewStep = panels.findIndex((panel) => panel.dataset.stepKind === 'overview');
    const overviewLanding = root.querySelector('[data-overview-landing]');
    const applicationLink = root.querySelector('[data-overview-application-link]');
    const applicationHeading = root.querySelector('[data-overview-application-heading]');
    const submitModal = root.querySelector('[data-submit-modal]');
    const submitModalDialog = root.querySelector('[data-submit-modal-dialog]');
    const submitModalSending = root.querySelector('[data-submit-modal-sending]');
    const submitModalSuccess = root.querySelector('[data-submit-modal-success]');
    const submitAgainButton = root.querySelector('[data-submit-again]');
    const endpoint = root.dataset.submissionEndpoint || '';
    let currentStep = Number(root.dataset.initialStep || 0);
    let confirmationUrls = [];
    let isSubmitting = false;
    let submitModalOpenedAt = 0;
    let submitModalCloseTimer;
    let conditionPhotos;

    function isOverviewCheckComplete(field) {
      if (field.type === 'radio') {
        return overviewChecks.some((option) => option.type === 'radio' && option.name === field.name && option.checked);
      }
      return field.checked;
    }

    function syncOverviewNext() {
      if (overviewNext) overviewNext.disabled = !overviewChecks.length || !overviewChecks.every(isOverviewCheckComplete);
    }

    function resetOverviewChecks() {
      overviewChecks.forEach((field) => { field.checked = false; });
      syncOverviewNext();
    }

    // A new request needs fresh confirmation; a login round-trip is the same request.
    resetOverviewChecks();
    const loginResume = setupLoginResume(root, overviewChecks, () => currentStep === basicStep);
    if (loginResume.restored && overviewChecks.every(isOverviewCheckComplete)) currentStep = basicStep;
    syncOverviewNext();
    overviewChecks.forEach((field) => field.addEventListener('change', () => {
      loginResume.clear();
      clearStepError(overviewStep);
      syncOverviewNext();
      if (conditionPhotos) conditionPhotos.sync();
    }));

    function syncSubmitButton() {
      const confirmed = termsConsent.isAgreed();
      submitButton.hidden = false;
      submitButton.disabled = !confirmed || isSubmitting || !endpoint;
      // Keep the hidden consent field successful in FormData while the visible action is locked.
      if (confirmationAcknowledgement) confirmationAcknowledgement.disabled = false;
      if (termsOpenButton) termsOpenButton.disabled = isSubmitting;
    }

    const termsConsent = setupTermsModal(root, () => {
      clearStepError(panels.length - 1);
      syncSubmitButton();
    });

    if (applicationLink && applicationHeading) applicationLink.addEventListener('click', (event) => {
      event.preventDefault();
      const heading = applicationHeading.querySelector('h2');
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      }
      applicationHeading.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    function showStep(step, options = {}) {
      const nextStep = Math.max(0, Math.min(step, panels.length - 1));
      if (nextStep !== currentStep) termsConsent.reset();
      currentStep = nextStep;
      const isOverview = nextStep === overviewStep;
      if (isOverview) root.setAttribute('data-overview-active', '');
      else root.removeAttribute('data-overview-active');
      if (overviewLanding) overviewLanding.hidden = !isOverview || Boolean(shop);
      syncSubmitButton();
      syncOverviewNext();

      panels.forEach((panel, index) => {
        const active = index === nextStep;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });

      indicators.forEach((indicator, index) => {
        indicator.classList.toggle('is-current', index === nextStep);
        indicator.classList.toggle('is-complete', index < nextStep);
        if (index === nextStep) {
          indicator.setAttribute('aria-current', 'step');
        } else {
          indicator.removeAttribute('aria-current');
        }
      });

      if (nextStep === customerStep) datePicker.refresh();
      if (nextStep === panels.length - 1) renderConfirmation();

      // Reveal only after the restored panel and progress indicators are in place.
      root.removeAttribute('data-login-restoring');
      if (options.focus !== false) {
        const heading = panels[nextStep].querySelector('h2');
        if (heading) {
          heading.setAttribute('tabindex', '-1');
          heading.focus({ preventScroll: true });
        }

        // Returning to STEP 01 should reveal its form, not scroll back through the landing page.
        const scrollTarget = isOverview && applicationHeading ? applicationHeading : root;
        const bounds = scrollTarget.getBoundingClientRect();
        if (bounds.top < 0 || bounds.top > window.innerHeight * 0.65) {
          scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }

    function clearStepError(step) {
      const panel = panels[step];
      if (!panel) return;
      const message = panel.querySelector('[data-step-error]');
      if (message) {
        message.hidden = true;
        message.textContent = '';
      }
      panel.querySelectorAll('[aria-invalid="true"]').forEach((field) => field.removeAttribute('aria-invalid'));
    }

    function setStepError(step, text, field) {
      const panel = panels[step];
      const message = panel && panel.querySelector('[data-step-error]');
      if (message) {
        message.textContent = text;
        message.hidden = false;
      }
      if (field) {
        field.setAttribute('aria-invalid', 'true');
        if (typeof field.focus === 'function') field.focus();
      }
    }

    function validateStep(step) {
      clearStepError(step);
      const panel = panels[step];
      if (!panel) return true;

      if (step === overviewStep && overviewChecks.length) {
        const unchecked = overviewChecks.find((field) => !isOverviewCheckComplete(field));
        if (unchecked) {
          setStepError(step, '確認事項をすべてご確認のうえ、選択・チェックをしてください。', unchecked);
          return false;
        }
      }

      if (step === panels.length - 1 && !termsConsent.isAgreed()) {
        setStepError(step, 'お申し込み前の注意事項をご確認のうえ、同意してください。', termsOpenButton);
        return false;
      }

      if (step === basicStep) {
        const missingPhoto = conditionPhotos && conditionPhotos.missing();
        if (missingPhoto) {
          setStepError(step, '「あり」を選択した項目の写真をそれぞれ1枚以上添付してください。', missingPhoto);
          return false;
        }
        const productError = productController.validationError();
        if (productError) {
          setStepError(step, productError, root.querySelector('[data-order-product-select]:not(:disabled)') || root.querySelector('[data-product-query]'));
          return false;
        }
      }

      const invalidField = Array.from(panel.querySelectorAll('input[required], select[required], textarea[required]'))
        .find((field) => !field.matches('[data-image-input], [data-condition-input]') && !field.disabled && !field.checkValidity());

      if (invalidField) {
        const message = invalidField.validity.typeMismatch
          ? '正しい形式のメールアドレスを入力してください。'
          : '必須項目を入力してください。';
        setStepError(step, message, invalidField);
        return false;
      }

      if (step === customerStep && !datePicker.validate()) {
        setStepError(step, '着用予定日は30日後以降を選択してください。', root.querySelector('[data-date-display]'));
        return false;
      }

      if (step === basicStep && productController.isUnknown() && attachmentController.files().length === 0) {
        setStepError(step, '品番不明の場合は、商品の画像を1枚以上添付してください。', root.querySelector('[data-image-input]'));
        return false;
      }

      return true;
    }

    root.querySelectorAll('[data-next-step]').forEach((button) => {
      button.addEventListener('click', () => {
        const panel = button.closest('[data-step-panel]');
        const step = Number(panel.dataset.stepPanel);
        if (validateStep(step)) showStep(step + 1);
      });
    });

    root.querySelectorAll('[data-prev-step]').forEach((button) => {
      button.addEventListener('click', () => {
        const panel = button.closest('[data-step-panel]');
        showStep(Number(panel.dataset.stepPanel) - 1);
      });
    });

    panels.forEach((panel, step) => {
      panel.addEventListener('input', (event) => {
        if (event.target.matches('input, select, textarea')) clearStepError(step);
      });
    });

    const postalCode = root.querySelector('[data-postal-code]');
    const prefecture = root.querySelector('[data-prefecture]');
    const municipality = root.querySelector('[data-municipality]');
    const postalStatus = root.querySelector('[data-postal-status]');
    let postalLookupController;
    let lastPostalLookup = '';

    function resetRestoredAddress() {
      if (!prefecture || !postalCode || !root.isConnected) return;
      const postalDigits = postalCode.value.replace(/\D/g, '');
      if (postalDigits.length < 7) {
        prefecture.value = '';
        if (municipality) municipality.value = '';
      }
    }

    resetRestoredAddress();
    window.setTimeout(resetRestoredAddress, 300);
    window.addEventListener('pageshow', resetRestoredAddress);

    async function lookupPostalAddress(postal) {
      if (!prefecture || !municipality || postal === lastPostalLookup) return;
      if (postalLookupController) postalLookupController.abort();

      const requestController = new AbortController();
      postalLookupController = requestController;
      lastPostalLookup = postal;
      postalStatus.textContent = '住所を検索中…';

      try {
        const url = new URL(POSTAL_LOOKUP_URL);
        url.searchParams.set('zipcode', postal);
        const response = await fetchWithTimeout(url.toString(), {
          headers: { Accept: 'application/json' },
          signal: requestController.signal
        });
        const payload = await response.json().catch(() => null);

        if (!response.ok || !payload || payload.status !== 200) {
          throw new Error(payload && payload.message ? payload.message : '住所検索に失敗しました。');
        }

        const address = Array.isArray(payload.results) ? payload.results[0] : null;
        if (!address) {
          prefecture.value = '';
          municipality.value = '';
          postalStatus.textContent = '該当する住所が見つかりませんでした。';
          return;
        }

        prefecture.value = address.address1 || '';
        municipality.value = `${address.address2 || ''}${address.address3 || ''}`;
        postalStatus.textContent = '都道府県と市区町を自動入力しました。';
      } catch (error) {
        if (error.name === 'AbortError') return;
        lastPostalLookup = '';
        postalStatus.textContent = '住所を自動入力できませんでした。手動で入力してください。';
      }
    }

    if (postalCode) {
      postalCode.addEventListener('input', () => {
        const normalized = postalCode.value
          .replace(/[０-９]/g, (character) => String.fromCharCode(character.charCodeAt(0) - 0xfee0))
          .replace(/\D/g, '')
          .slice(0, 7);
        postalCode.value = normalized.length > 3
          ? `${normalized.slice(0, 3)}-${normalized.slice(3)}`
          : normalized;

        if (normalized.length === 7) {
          lookupPostalAddress(normalized);
        } else {
          if (postalLookupController) postalLookupController.abort();
          lastPostalLookup = '';
          if (postalStatus) postalStatus.textContent = '';
        }
      });
    }

    const datePicker = setupCustomDatePicker(root);
    const attachmentController = setupImageAttachments(root);
    conditionPhotos = setupConditionPhotos(root, () => clearStepError(basicStep));
    const productController = setupProductSearch(root, (unknown) => {
      clearStepError(basicStep);
      clearStepError(customerStep);
      // Do not retain or submit photos from a now-hidden unknown-product field.
      if (!unknown && root.querySelector('[data-unknown-product-photos]')) attachmentController.reset();
    });
    const orderHistory = setupOrderHistory(root, productController);
    setupLoginModal(root);
    if (!loginResume.restored) setupEntryModal(root);

    function getConfirmationValue(field) {
      let value = String(field.value || '').trim();

      if (field.tagName === 'SELECT' && value) {
        const selectedOption = field.options[field.selectedIndex];
        value = selectedOption ? selectedOption.textContent.trim() : value;
      }

      if ((field.type === 'date' || field.hasAttribute('data-confirm-date')) && value) {
        const parts = value.split('-').map(Number);
        if (parts.length === 3 && parts.every(Number.isFinite)) {
          value = `${parts[0]}年${parts[1]}月${parts[2]}日`;
        }
      }

      const suffix = field.dataset.confirmSuffix || '';
      return value ? `${value}${suffix}` : '—';
    }

    function createConfirmationRow(label) {
      const row = document.createElement('div');
      const term = document.createElement('dt');
      const description = document.createElement('dd');
      row.className = 'cleaning-service__confirmation-row';
      term.textContent = label;
      row.append(term, description);
      confirmation.appendChild(row);
      return description;
    }

    function renderConfirmation() {
      if (!confirmation) return;

      confirmationUrls.forEach((url) => URL.revokeObjectURL(url));
      confirmationUrls = [];
      confirmation.replaceChildren();

      const confirmationFields = Array.from(form.querySelectorAll('[data-confirm-label]'));
      function renderField(field) {
        if (field.disabled) return;
        if ((field.type === 'radio' || field.type === 'checkbox') && !field.checked) return;
        const description = createConfirmationRow(field.dataset.confirmLabel);
        description.textContent = getConfirmationValue(field);
      }
      confirmationFields.filter((field) => !overviewChecks.includes(field)).forEach(renderField);

      conditionPhotos.groups().forEach((group) => {
        const description = createConfirmationRow(group.label);
        description.textContent = group.enabled ? 'あり' : 'なし';
        if (!group.enabled) return;
        const list = document.createElement('div');
        list.className = 'cleaning-service__confirmation-images';
        group.files().forEach((file) => {
          const figure = document.createElement('figure');
          const image = document.createElement('img');
          const caption = document.createElement('figcaption');
          const url = URL.createObjectURL(file);
          confirmationUrls.push(url);
          figure.className = 'cleaning-service__confirmation-image';
          image.src = url;
          image.alt = group.label;
          caption.textContent = file.name;
          figure.append(image, caption);
          list.appendChild(figure);
        });
        description.appendChild(list);
      });

      // Keep the overview acknowledgements directly below the two condition/photo rows.
      confirmationFields.filter((field) => overviewChecks.includes(field)).forEach(renderField);

      const files = attachmentController.files();
      const imageDescription = createConfirmationRow('添付画像');

      if (!files.length) {
        imageDescription.textContent = '—';
        return;
      }

      const imageList = document.createElement('div');
      imageList.className = 'cleaning-service__confirmation-images';
      files.forEach((file, index) => {
        const figure = document.createElement('figure');
        const image = document.createElement('img');
        const caption = document.createElement('figcaption');
        const url = URL.createObjectURL(file);

        confirmationUrls.push(url);
        figure.className = 'cleaning-service__confirmation-image';
        image.src = url;
        image.alt = `添付画像 ${index + 1}`;
        caption.textContent = file.name;
        figure.append(image, caption);
        imageList.appendChild(figure);
      });
      imageDescription.appendChild(imageList);
    }

    function validateAllSteps() {
      for (let step = 0; step < panels.length; step += 1) {
        if (!validateStep(step)) {
          showStep(step, { focus: false });
          const invalid = panels[step].querySelector('[aria-invalid="true"]');
          if (invalid && typeof invalid.focus === 'function') invalid.focus();
          return false;
        }
      }
      return true;
    }

    function setSubmitStatus(message, type) {
      submitStatus.textContent = message;
      submitStatus.className = `cleaning-service__notice cleaning-service__notice--${type}`;
      submitStatus.hidden = false;
    }

    function openSubmitModal() {
      if (!submitModal) return;
      window.clearTimeout(submitModalCloseTimer);
      if (submitModalSending) submitModalSending.hidden = false;
      if (submitModalSuccess) submitModalSuccess.hidden = true;
      submitModal.classList.remove('is-complete');
      if (submitModalDialog) {
        const sendingTitle = submitModalSending && submitModalSending.querySelector('.cleaning-service-submit-modal__title');
        const sendingText = submitModalSending && submitModalSending.querySelector('.cleaning-service-submit-modal__text');
        submitModalDialog.setAttribute('aria-busy', 'true');
        if (sendingTitle) submitModalDialog.setAttribute('aria-labelledby', sendingTitle.id);
        if (sendingText) submitModalDialog.setAttribute('aria-describedby', sendingText.id);
      }
      submitModalOpenedAt = performance.now();
      submitModal.hidden = false;
      submitModal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('cleaning-service-submit-modal-open');

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          submitModal.classList.add('is-open');
          if (submitModalDialog) submitModalDialog.focus({ preventScroll: true });
        });
      });
    }

    function showSubmitSuccessModal() {
      if (!submitModal || !submitModalSuccess) return;
      if (submitModalSending) submitModalSending.hidden = true;
      submitModalSuccess.hidden = false;
      submitModal.classList.add('is-complete');

      if (submitModalDialog) {
        const successTitle = submitModalSuccess.querySelector('.cleaning-service-submit-modal__title');
        const successText = submitModalSuccess.querySelector('.cleaning-service-submit-modal__text');
        submitModalDialog.setAttribute('aria-busy', 'false');
        if (successTitle) submitModalDialog.setAttribute('aria-labelledby', successTitle.id);
        if (successText) submitModalDialog.setAttribute('aria-describedby', successText.id);
      }

      window.requestAnimationFrame(() => {
        if (submitAgainButton) submitAgainButton.focus({ preventScroll: true });
      });
    }

    async function waitForSubmitModalAnimation() {
      if (!submitModalOpenedAt) return;
      const remaining = SUBMIT_MODAL_MIN_VISIBLE - (performance.now() - submitModalOpenedAt);
      if (remaining > 0) {
        await new Promise((resolve) => window.setTimeout(resolve, remaining));
      }
    }

    function closeSubmitModal() {
      if (!submitModal) return;
      submitModal.classList.remove('is-open');
      submitModal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cleaning-service-submit-modal-open');
      submitModalOpenedAt = 0;
      window.clearTimeout(submitModalCloseTimer);
      submitModalCloseTimer = window.setTimeout(() => {
        submitModal.hidden = true;
        submitModal.classList.remove('is-complete');
      }, 420);
    }

    if (submitModal) {
      submitModal.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          return;
        }
        if (event.key !== 'Tab') return;

        const focusable = Array.from(submitModal.querySelectorAll('a[href], button:not([disabled])'))
          .filter((element) => !element.hidden && element.offsetParent !== null);
        if (!focusable.length) {
          event.preventDefault();
          return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    }

    if (submitAgainButton) {
      submitAgainButton.addEventListener('click', () => {
        submitStatus.hidden = true;
        closeSubmitModal();
        showStep(0, { focus: false });
        root.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.setTimeout(() => {
          const firstField = panels[0] && panels[0].querySelector('button, input:not([type="hidden"]), select, textarea');
          if (firstField) firstField.focus({ preventScroll: true });
        }, 120);
      });
    }

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (isSubmitting) {
        return;
      }

      // Enter on an input advances the current step; only confirmation may send.
      if (currentStep < panels.length - 1) {
        if (validateStep(currentStep)) showStep(currentStep + 1);
        return;
      }

      if (!validateAllSteps()) {
        return;
      }

      if (!endpoint) {
        setSubmitStatus('現在、クリーニングのお申込みは準備中です。', 'error');
        return;
      }

      isSubmitting = true;
      syncSubmitButton();
      submitButton.setAttribute('aria-busy', 'true');
      submitLabel.textContent = '送信中…';
      submitStatus.hidden = true;
      openSubmitModal();
      let submissionSucceeded = false;

      try {
        const formData = new FormData(form);
        // Radio names only group the condition choices; the two confirmations are submitted.
        overviewChecks.forEach((field) => { if (field.type === 'radio' && field.name) formData.delete(field.name); });
        await conditionPhotos.prepareSubmission(formData, endpoint);
        formData.delete('images[]');
        attachmentController.files().forEach((file) => formData.append('images[]', file, file.name));

        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData
        });

        let responseBody = null;
        if ((response.headers.get('content-type') || '').includes('application/json')) {
          responseBody = await response.json().catch(() => null);
        }

        if (!response.ok || (responseBody && responseBody.success === false)) {
          throw new Error(responseBody && responseBody.message ? responseBody.message : '送信に失敗しました。');
        }

        form.reset();
        loginResume.clear();
        resetOverviewChecks();
        conditionPhotos.reset();
        attachmentController.reset();
        productController.reset();
        orderHistory.reset();
        panels.forEach((panel, step) => clearStepError(step));
        showStep(0, { focus: false });
        setSubmitStatus(responseBody && responseBody.message ? responseBody.message : root.dataset.successMessage, 'success');
        submissionSucceeded = true;
      } catch (error) {
        setSubmitStatus(error.message || '送信できませんでした。時間をおいて再度お試しください。', 'error');
      } finally {
        await waitForSubmitModalAnimation();
        if (submissionSucceeded) showSubmitSuccessModal();
        else closeSubmitModal();
        isSubmitting = false;
        syncSubmitButton();
        submitButton.removeAttribute('aria-busy');
        submitLabel.textContent = 'クリーニング依頼';
        if (!submissionSucceeded) {
          submitStatus.setAttribute('tabindex', '-1');
          submitStatus.focus({ preventScroll: true });
        }
      }
    });

    showStep(currentStep, { focus: loginResume.restored && !root.querySelector('[data-login-errors]') });
  }

  function setupCustomDatePicker(root) {
    const picker = root.querySelector('[data-custom-date-picker]');
    if (!picker) return;

    const displayInput = picker.querySelector('[data-date-display]');
    const valueInput = picker.querySelector('[data-wear-date]');
    const toggleButton = picker.querySelector('[data-date-toggle]');
    const calendar = picker.querySelector('[data-date-calendar]');
    const monthLabel = picker.querySelector('[data-date-month]');
    const dateGrid = picker.querySelector('[data-date-grid]');
    const previousButton = picker.querySelector('[data-date-prev]');
    const nextButton = picker.querySelector('[data-date-next]');
    const clearButton = picker.querySelector('[data-date-clear]');
    if (!displayInput || !valueInput || !toggleButton || !calendar || !monthLabel || !dateGrid) return;

    let today = parseDateKey(japanDateKey());
    let minimumDate = parseDateKey(minimumWearDateKey());

    function refreshDateLimit() {
      today = parseDateKey(japanDateKey());
      minimumDate = parseDateKey(minimumWearDateKey());
      const hint = picker.querySelector('[data-wear-date-hint]');
      if (hint) {
        hint.textContent = `30日後（${minimumDate.getFullYear()}年${minimumDate.getMonth() + 1}月${minimumDate.getDate()}日）から選択できます。`;
      }
    }

    function dateKey(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    function parseDateKey(value) {
      const parts = String(value || '').split('-').map(Number);
      if (parts.length !== 3 || !parts.every(Number.isFinite)) return null;
      const date = new Date(parts[0], parts[1] - 1, parts[2]);
      return Number.isNaN(date.getTime()) || dateKey(date) !== value ? null : date;
    }

    function sameDate(left, right) {
      return Boolean(left && right) && dateKey(left) === dateKey(right);
    }

    let selectedDate = parseDateKey(valueInput.value);
    if (selectedDate && selectedDate < minimumDate) selectedDate = null;
    let visibleMonth = new Date(
      (selectedDate || minimumDate).getFullYear(),
      (selectedDate || minimumDate).getMonth(),
      1
    );

    function updateDisplay() {
      displayInput.setCustomValidity('');
      valueInput.value = selectedDate ? dateKey(selectedDate) : '';
      displayInput.value = selectedDate
        ? `${selectedDate.getFullYear()}年${selectedDate.getMonth() + 1}月${selectedDate.getDate()}日`
        : '';
    }

    function closeCalendar(returnFocus = false) {
      calendar.hidden = true;
      picker.classList.remove('is-open');
      displayInput.setAttribute('aria-expanded', 'false');
      toggleButton.setAttribute('aria-expanded', 'false');
      if (returnFocus) toggleButton.focus();
    }

    function renderCalendar() {
      refreshDateLimit();
      const earliestMonth = new Date(minimumDate.getFullYear(), minimumDate.getMonth(), 1);
      if (visibleMonth < earliestMonth) visibleMonth = earliestMonth;
      const year = visibleMonth.getFullYear();
      const month = visibleMonth.getMonth();
      const firstWeekday = new Date(year, month, 1).getDay();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      monthLabel.textContent = `${year}年 ${month + 1}月`;
      dateGrid.replaceChildren();

      if (previousButton) {
        previousButton.disabled = visibleMonth <= earliestMonth;
      }

      for (let index = 0; index < firstWeekday; index += 1) {
        const blank = document.createElement('span');
        blank.className = 'cleaning-service__calendar-blank';
        blank.setAttribute('aria-hidden', 'true');
        dateGrid.appendChild(blank);
      }

      for (let day = 1; day <= daysInMonth; day += 1) {
        const date = new Date(year, month, day);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'cleaning-service__calendar-day';
        button.textContent = String(day);
        button.dataset.dateValue = dateKey(date);
        button.setAttribute('aria-label', `${year}年${month + 1}月${day}日`);
        button.disabled = date < minimumDate;
        button.classList.toggle('is-today', sameDate(date, today));
        button.classList.toggle('is-selected', sameDate(date, selectedDate));
        button.setAttribute('aria-pressed', sameDate(date, selectedDate) ? 'true' : 'false');
        button.addEventListener('click', () => {
          if (!isAllowedWearDate(dateKey(date))) {
            renderCalendar();
            return;
          }
          selectedDate = date;
          updateDisplay();
          valueInput.dispatchEvent(new Event('input', { bubbles: true }));
          valueInput.dispatchEvent(new Event('change', { bubbles: true }));
          closeCalendar(true);
        });
        dateGrid.appendChild(button);
      }
    }

    function openCalendar() {
      calendar.hidden = false;
      picker.classList.add('is-open');
      displayInput.setAttribute('aria-expanded', 'true');
      toggleButton.setAttribute('aria-expanded', 'true');
      renderCalendar();
      window.requestAnimationFrame(() => {
        const focusTarget = calendar.querySelector('.is-selected:not(:disabled), .is-today:not(:disabled), .cleaning-service__calendar-day:not(:disabled)');
        if (focusTarget) focusTarget.focus();
      });
    }

    function toggleCalendar() {
      if (calendar.hidden) openCalendar();
      else closeCalendar();
    }

    displayInput.addEventListener('click', toggleCalendar);
    displayInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        toggleCalendar();
      }
    });
    toggleButton.addEventListener('click', toggleCalendar);

    if (previousButton) {
      previousButton.addEventListener('click', () => {
        visibleMonth = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() - 1, 1);
        renderCalendar();
      });
    }

    if (nextButton) {
      nextButton.addEventListener('click', () => {
        visibleMonth = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 1);
        renderCalendar();
      });
    }

    if (clearButton) {
      clearButton.addEventListener('click', () => {
        selectedDate = null;
        refreshDateLimit();
        visibleMonth = new Date(minimumDate.getFullYear(), minimumDate.getMonth(), 1);
        updateDisplay();
        valueInput.dispatchEvent(new Event('input', { bubbles: true }));
        valueInput.dispatchEvent(new Event('change', { bubbles: true }));
        closeCalendar(true);
      });
    }

    calendar.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeCalendar(true);
      }
    });

    document.addEventListener('click', (event) => {
      if (!picker.contains(event.target)) closeCalendar();
    });

    const form = picker.closest('form');
    if (form) {
      form.addEventListener('reset', () => {
        window.requestAnimationFrame(() => {
          selectedDate = null;
          refreshDateLimit();
          visibleMonth = new Date(minimumDate.getFullYear(), minimumDate.getMonth(), 1);
          updateDisplay();
          closeCalendar();
        });
      });
    }

    updateDisplay();
    renderCalendar();
    return {
      refresh: renderCalendar,
      validate: () => {
        refreshDateLimit();
        const valid = isAllowedWearDate(valueInput.value);
        displayInput.setCustomValidity(valid ? '' : '着用予定日は登録日の30日後以降を選択してください。');
        return valid;
      }
    };
  }

  function setupShopVariant(root) {
    const shop = new URLSearchParams(window.location.search).get('shop');
    if (!['osaka', 'jiyugaoka', 'popup'].includes(shop)) {
      root.removeAttribute('data-cleaning-shop');
      return '';
    }
    root.setAttribute('data-cleaning-shop', shop);
    const destination = `https://tweed-repair-request-mailer.app-admin-c1e.workers.dev/staff-cleaning?shop=${shop}`;
    const link = root.querySelector('[data-staff-login-link]');
    if (link) link.setAttribute('href', destination);
    window.location.replace(destination);
    return shop;
  }

  function setupLoginResume(root, checks, isBasicStep) {
    // Only overview choices are stored, in this tab and for this page/section.
    // Never serialize the login form, contact information or attached files.
    const maxAge = 30 * 60 * 1000;
    const key = `cleaning-login-resume:${window.location.pathname}:${root.dataset.sectionId}`;
    const signature = JSON.stringify(checks.map((field) => [field.type, field.name || '', field.value || '']));
    let restored = false;

    function clear() {
      try { window.sessionStorage.removeItem(key); } catch (_) { /* Storage can be disabled. */ }
    }

    try {
      const saved = JSON.parse(window.sessionStorage.getItem(key));
      // Consume once: a later visit or a new request must start with fresh checks.
      clear();
      if (saved && saved.signature === signature && Number.isFinite(saved.savedAt)
        && Date.now() >= saved.savedAt && Date.now() - saved.savedAt <= maxAge
        && Array.isArray(saved.checked) && saved.checked.length === checks.length
        && saved.checked.every((value) => typeof value === 'boolean')) {
        const radioGroups = new Set();
        const valid = checks.every((field, index) => {
          if (field.type !== 'radio' || !saved.checked[index]) return true;
          if (radioGroups.has(field.name)) return false;
          radioGroups.add(field.name);
          return true;
        });
        if (valid) {
          checks.forEach((field, index) => { field.checked = saved.checked[index]; });
          restored = true;
        }
      }
    } catch (_) { clear(); }

    function save() {
      if (!isBasicStep()) return;
      try {
        window.sessionStorage.setItem(key, JSON.stringify({
          signature, savedAt: Date.now(), checked: checks.map((field) => Boolean(field.checked))
        }));
      } catch (_) { /* Keep Shopify login usable even when storage is unavailable. */ }
    }

    const loginForm = root.querySelector('[data-cleaning-login-form]');
    // Observe the native POST before CAPTCHA handlers; never cancel or replace it.
    if (loginForm) loginForm.addEventListener('submit', save, true);
    root.querySelectorAll('[data-open-login], .cleaning-service-login-modal__recover')
      .forEach((link) => link.addEventListener('click', save));
    const loginModal = root.querySelector('[data-login-modal]');
    const closeButton = root.querySelector('[data-close-login]');
    if (closeButton) closeButton.addEventListener('click', clear);
    if (loginModal) {
      loginModal.addEventListener('click', (event) => { if (event.target === loginModal) clear(); });
      loginModal.addEventListener('keydown', (event) => { if (event.key === 'Escape') clear(); });
    }
    return { restored, clear };
  }

  function setupLoginModal(root) {
    const trigger = root.querySelector('[data-open-login]');
    const modal = root.querySelector('[data-login-modal]');
    if (!trigger || !modal) return;
    const dialog = modal.querySelector('[data-login-dialog]');
    const email = modal.querySelector('[data-login-email]');
    const password = modal.querySelector('[data-login-password]');
    const closeButton = modal.querySelector('[data-close-login]');
    const loginForm = modal.querySelector('[data-cleaning-login-form]');
    const submitButton = modal.querySelector('[data-login-submit]');
    const submitLabel = modal.querySelector('[data-login-submit-label]');
    const spinner = modal.querySelector('[data-login-spinner]');
    const inquiryContent = root.querySelector('.cleaning-service__inner');
    const inquiryEmail = root.querySelector('input[name="contact[email]"]');
    let closeTimer;
    let previouslyInert = false;
    let loadingTimer;

    function resetLoading() {
      window.clearTimeout(loadingTimer);
      if (submitButton) submitButton.setAttribute('aria-busy', 'false');
      if (submitLabel) submitLabel.textContent = 'ログイン';
      if (spinner) spinner.hidden = true;
    }

    function showLoading() {
      resetLoading();
      if (submitButton) submitButton.setAttribute('aria-busy', 'true');
      if (submitLabel) submitLabel.textContent = 'ログイン中…';
      if (spinner) spinner.hidden = false;
      // CAPTCHA or a cancelled navigation must never leave a permanent loading state.
      loadingTimer = window.setTimeout(resetLoading, 30000);
    }

    function open() {
      window.clearTimeout(closeTimer);
      resetLoading();
      if (modal.hidden) previouslyInert = Boolean(inquiryContent && inquiryContent.inert);
      modal.hidden = false;
      if (inquiryContent) inquiryContent.inert = true;
      trigger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('cleaning-service-login-modal-open');
      if (!email.value && inquiryEmail) email.value = inquiryEmail.value;
      window.requestAnimationFrame(() => {
        if (trigger.getAttribute('aria-expanded') !== 'true') return;
        modal.classList.add('is-open');
        email.focus({ preventScroll: true });
      });
    }

    function close() {
      resetLoading();
      modal.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
      // Do not retain a password after dismissing the dialog.
      password.value = '';
      if (inquiryContent) inquiryContent.inert = previouslyInert;
      document.body.classList.remove('cleaning-service-login-modal-open');
      trigger.focus({ preventScroll: true });
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(() => { modal.hidden = true; }, 200);
    }

    trigger.addEventListener('click', (event) => {
      // Preserve the real login URL for new tabs and browsers without this script.
      if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button > 0) return;
      event.preventDefault();
      open();
    });
    closeButton.addEventListener('click', close);
    modal.addEventListener('click', (event) => {
      if (event.target === modal) close();
    });
    modal.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      // Keep normal tab navigation in the dialog; do not trap external CAPTCHA challenges.
      const focusable = Array.from(dialog.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), iframe'))
        .filter((element) => !element.hidden && element.offsetParent !== null);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first) { event.preventDefault(); return; }
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    });
    // Visual feedback only: do not cancel native POST, disable credentials/the
    // submitter, or interfere with Shopify's CAPTCHA submission/retry handlers.
    if (loginForm) {
      loginForm.addEventListener('submit', showLoading, true);
      loginForm.addEventListener('invalid', resetLoading, true);
      loginForm.addEventListener('input', resetLoading);
      loginForm.addEventListener('reset', resetLoading);
    }
    window.addEventListener('pageshow', resetLoading);
    resetLoading();
    if (modal.querySelector('[data-login-errors]')) open();
  }

  function setupOrderHistory(root, productController) {
    const history = root.querySelector('[data-order-history]');
    if (!history || !productController) return { reset() {} };
    const number = root.querySelector('[data-order-number]');
    const ordersSelect = history.querySelector('[data-order-select]');
    const productField = root.querySelector('[data-order-product-field]');
    const productsSelect = root.querySelector('[data-order-product-select]');
    const productPreview = root.querySelector('[data-order-product-preview]');
    const productImage = root.querySelector('[data-order-product-image]');
    const productName = root.querySelector('[data-order-product-name]');
    const manualField = root.querySelector('[data-manual-product-field]');
    const unknownInput = root.querySelector('[data-product-unknown]');
    const unknownField = root.querySelector('.cleaning-service__unknown-product');
    const unknownPhotos = root.querySelector('[data-unknown-product-photos]');
    // Share the checkbox and inline photo upload with order mode.
    // Guests keep the original position inside the manual search field.
    if (unknownField) {
      manualField.after(unknownField);
      if (unknownPhotos) unknownField.after(unknownPhotos);
    }
    const more = history.querySelector('[data-orders-more]');
    const status = history.querySelector('[data-orders-status]');
    const orders = new Map();
    let nextUrl = '';
    let pendingRequest;

    function option(value, label) {
      const node = document.createElement('option');
      node.value = value;
      node.textContent = label;
      return node;
    }

    function showProductPreview(product) {
      if (!productPreview || !productImage || !productName) return;
      productPreview.hidden = true;
      productImage.removeAttribute('src');
      productName.textContent = '';
      if (!product || !product.image) return;
      productImage.src = product.image;
      productName.textContent = product.title;
      productPreview.hidden = false;
    }

    if (productImage) productImage.addEventListener('error', () => showProductPreview());
    showProductPreview();

    function clearProducts() {
      // Also cancels pending manual searches before their UI is hidden.
      productController.reset();
      productsSelect.replaceChildren(option('', '商品を選択してください'));
    }

    function syncProductMode() {
      const hasOrder = orders.has(ordersSelect.value);
      const manual = !hasOrder;
      manualField.hidden = !manual;
      productController.setManualMode(manual);
      productField.hidden = manual;
      productsSelect.disabled = manual || unknownInput.checked || !(orders.get(ordersSelect.value) || []).length;
      productsSelect.required = !manual && !unknownInput.checked;
    }

    function readOrders(source) {
      source.querySelectorAll('[data-history-order]').forEach((node) => {
        const name = node.dataset.number;
        if (!name || orders.has(name)) return;
        const products = [];
        const seen = new Set();
        node.querySelectorAll('[data-history-product]').forEach((item) => {
          const title = item.dataset.title;
          const handle = /^[a-z0-9][a-z0-9-]*$/i.test(item.dataset.handle || '') ? item.dataset.handle : '';
          const key = handle || title;
          if (!title || seen.has(key)) return;
          seen.add(key);
          products.push({ title, url: handle ? '/products/' + handle : '', image: item.dataset.image || '' });
        });
        orders.set(name, products);
        ordersSelect.appendChild(option(name, node.dataset.label || name));
      });
      nextUrl = source.dataset.nextUrl || '';
      more.hidden = !nextUrl;
      status.textContent = orders.size ? '' : '注文履歴がありません。注文を選択せずにお進みいただけます。';
    }

    const initialSource = history.querySelector('[data-order-history-source]');
    if (initialSource) {
      readOrders(initialSource);
      initialSource.remove();
    }
    syncProductMode();

    ordersSelect.addEventListener('change', () => {
      clearProducts();
      number.value = ordersSelect.value;
      const titles = orders.get(ordersSelect.value) || [];
      titles.forEach((product, index) => {
        const item = option(String(index), product.title + (product.url ? '' : '（選択できません）'));
        item.disabled = !product.url;
        productsSelect.appendChild(item);
      });
      if (titles.some(product => !product.url)) {
        status.textContent = '現在の商品情報がない商品は選択できません。品番不明を選ぶか、注文番号の選択を解除して品番検索をご利用ください。';
      } else {
        status.textContent = '';
      }
      if (orders.has(ordersSelect.value) && !titles.length) {
        productsSelect.replaceChildren(option('', 'この注文に商品情報がありません'));
      }
      syncProductMode();
    });

    number.addEventListener('input', () => {
      if (number.value !== ordersSelect.value) {
        ordersSelect.value = '';
        clearProducts();
        syncProductMode();
      }
    });

    productsSelect.addEventListener('change', async () => {
      if (productsSelect.disabled) return;
      const titles = orders.get(ordersSelect.value) || [];
      const product = productsSelect.value === '' ? null : titles[Number(productsSelect.value)];
      if (!product || !product.url) {
        productController.reset();
        return;
      }
      showProductPreview(product);
      await productController.select(product, 'order');
    });

    productController.onChange((source) => {
      if (source === 'order') return;
      productsSelect.value = '';
      showProductPreview();
      syncProductMode();
    });

    more.addEventListener('click', async () => {
      if (!nextUrl || pendingRequest) return;
      const controller = new AbortController();
      pendingRequest = controller;
      more.disabled = true;
      status.textContent = '注文履歴を読み込み中…';
      try {
        const url = new URL(nextUrl, window.location.href);
        if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) {
          throw new Error('Invalid order history URL');
        }
        url.searchParams.set('section_id', root.dataset.sectionId);
        const response = await fetchWithTimeout(url.toString(), {
          credentials: 'same-origin', cache: 'no-store', redirect: 'error',
          headers: { Accept: 'text/html' }, signal: controller.signal
        });
        if (!response.ok) throw new Error('Order history unavailable');
        const html = await response.text();
        if (controller.signal.aborted || pendingRequest !== controller) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const source = doc.querySelector('[data-order-history-source]');
        if (!source) throw new Error('Order history session unavailable');
        const previousSize = orders.size;
        readOrders(source);
        status.textContent = `${orders.size - previousSize}件の注文を追加しました。`;
      } catch (error) {
        if (controller.signal.aborted) return;
        status.textContent = '注文履歴を読み込めませんでした。ログイン状態を確認して再度お試しください。注文を選択せずにお進みいただくこともできます。';
      } finally {
        if (pendingRequest === controller) {
          pendingRequest = null;
          more.disabled = false;
        }
      }
    });

    return {
      reset() {
        if (pendingRequest) pendingRequest.abort();
        pendingRequest = null;
        more.disabled = false;
        ordersSelect.value = '';
        number.value = '';
        clearProducts();
        syncProductMode();
        status.textContent = orders.size ? '' : '注文履歴がありません。注文を選択せずにお進みいただけます。';
      }
    };
  }

  function setupProductSearch(root, onChange) {
    const search = root.querySelector('[data-product-search]');
    const input = search.querySelector('[data-product-query]');
    const unknownInput = search.querySelector('[data-product-unknown]');
    const results = search.querySelector('[data-product-results]');
    const status = search.querySelector('[data-search-status]');
    const statusSlot = search.querySelector('[data-search-status-slot]');
    const selected = search.querySelector('[data-selected-product]');
    const selectedCopy = search.querySelector('[data-selected-product-copy]');
    const selectedImage = search.querySelector('[data-selected-product-image]');
    const selectedName = search.querySelector('[data-selected-product-name]');
    const clearButton = search.querySelector('[data-clear-product]');
    const searchButton = search.querySelector('[data-product-search-button]');
    const titleField = search.querySelector('[data-product-title]');
    const handleField = search.querySelector('[data-product-handle]');
    const priceField = root.querySelector('[data-cleaning-price]');
    const productSummary = root.querySelector('[data-customer-product-name]');
    const priceSummary = root.querySelector('[data-customer-cleaning-price]');
    const imageInput = root.querySelector('[data-image-input]');
    const imageRequirement = root.querySelector('[data-image-requirement]');
    const imageRequiredHint = root.querySelector('[data-image-required-hint]');
    const unknownPhotos = root.querySelector('[data-unknown-product-photos]');
    const searchUrl = root.dataset.searchUrl || '/search';
    let debounceTimer;
    let abortController;
    let detailsController;
    let products = [];
    let activeIndex = -1;
    let selectedProduct = null;
    let priceLabel = '';
    let priceState = 'idle';
    let manualMode = true;
    const selectionListeners = [];
    function notifySelection(source) { selectionListeners.forEach(listener => listener(source)); }

    function showSelectedImage(product) {
      if (!selectedImage) return;
      selectedImage.hidden = true;
      selectedImage.removeAttribute('src');
      const imageUrl = product && ((product.featured_image && product.featured_image.url) || product.image);
      if (typeof imageUrl !== 'string' || !imageUrl) return;
      selectedImage.src = imageUrl;
      selectedImage.hidden = false;
    }

    if (selectedImage) selectedImage.addEventListener('error', () => showSelectedImage());

    function closeResults() {
      results.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      activeIndex = -1;
    }

    function cancelRequests() {
      window.clearTimeout(debounceTimer);
      if (abortController) abortController.abort();
      if (detailsController) detailsController.abort();
      abortController = null;
      detailsController = null;
      products = [];
      results.replaceChildren();
      closeResults();
      searchButton.disabled = !manualMode || unknownInput.checked;
    }

    function syncSelection() {
      const unknown = unknownInput.checked;
      input.disabled = !manualMode || unknown;
      input.required = manualMode && !unknown;
      unknownInput.disabled = false;
      searchButton.disabled = !manualMode || unknown || Boolean(abortController);
      titleField.value = unknown ? UNKNOWN_PRODUCT_NAME : (selectedProduct ? selectedProduct.title : '');
      if (handleField) {
        handleField.value = '';
        if (!unknown && selectedProduct && selectedProduct.url) {
          const match = new URL(selectedProduct.url, window.location.origin).pathname.match(/\/products\/([^/]+)\/?$/);
          if (match) handleField.value = match[1];
        }
      }
      priceField.value = unknown ? UNKNOWN_CLEANING_PRICE : priceLabel;
      selected.hidden = unknown || !selectedProduct;
      selectedName.textContent = selectedProduct ? selectedProduct.title : '';
      // Keep one live status node: group the selected manual product and fee,
      // but leave search messages and order-mode fees outside the hidden card.
      const statusTarget = manualMode && selectedProduct && !unknown ? selectedCopy : statusSlot;
      if (statusTarget && status.parentElement !== statusTarget) statusTarget.appendChild(status);
      productSummary.textContent = titleField.value || '未選択';
      priceSummary.textContent = priceField.value || (
        priceState === 'loading' ? '料金を確認中…'
          : priceState === 'error' || priceState === 'missing' ? '料金を確認できません' : '—'
      );
      imageInput.required = unknown;
      imageInput.setAttribute('aria-required', String(unknown));
      if (unknownPhotos) {
        unknownPhotos.hidden = !unknown;
        imageInput.disabled = !unknown;
      }
      imageRequirement.textContent = unknown ? '必須' : '任意';
      imageRequirement.className = unknown ? 'cleaning-service__required' : 'cleaning-service__optional';
      imageRequiredHint.hidden = !unknown;
      if (onChange) onChange(unknown);
    }

    function clearSelection(keepQuery = false) {
      cancelRequests();
      selectedProduct = null;
      showSelectedImage();
      priceLabel = '';
      priceState = 'idle';
      if (!keepQuery) input.value = '';
      status.textContent = '';
      syncSelection();
      notifySelection('clear');
    }

    function setActiveResult(index) {
      const options = Array.from(results.querySelectorAll('[role="option"]'));
      if (!options.length) return;
      activeIndex = (index + options.length) % options.length;
      options.forEach((option, optionIndex) => {
        option.classList.toggle('is-focused', optionIndex === activeIndex);
        option.setAttribute('aria-selected', optionIndex === activeIndex ? 'true' : 'false');
      });
      input.setAttribute('aria-activedescendant', options[activeIndex].id);
      options[activeIndex].scrollIntoView({ block: 'nearest' });
    }

    async function chooseProduct(product, source = 'manual') {
      if (unknownInput.checked || !product || typeof product.title !== 'string') return;
      cancelRequests();
      selectedProduct = product;
      showSelectedImage(product);
      notifySelection(source);
      input.value = product.title;
      priceLabel = '';
      priceState = 'loading';
      status.textContent = 'クリーニング料金を確認中…';
      syncSelection();
      const controller = new AbortController();
      detailsController = controller;
      try {
        let cleaningType;
        if (Object.prototype.hasOwnProperty.call(product, 'cleaning_type')) {
          cleaningType = product.cleaning_type;
        } else {
          // Read Liquid's metafield value without requiring a Storefront API token.
          const productUrl = new URL(product.url, window.location.origin);
          if (!/^\/(?:[^/]+\/)?products\/[^/]+\/?$/.test(productUrl.pathname)) {
            throw new Error('Invalid product URL');
          }
          const detailUrl = new URL(productUrl.pathname, window.location.origin);
          detailUrl.searchParams.set('view', 'cleaning-details');
          const response = await fetchWithTimeout(detailUrl.toString(), {
            // Shopify renders the Liquid view only for HTML requests. JSON
            // negotiation returns its standard product data without metafields.
            headers: { Accept: 'text/html' }, signal: controller.signal
          });
          if (!response.ok) throw new Error('Product details unavailable');
          const payload = await response.json();
          if (!payload || !Object.prototype.hasOwnProperty.call(payload, 'cleaning_type')) {
            throw new Error('Invalid product details');
          }
          cleaningType = payload.cleaning_type;
        }
        if (detailsController !== controller || controller.signal.aborted || unknownInput.checked) return;
        priceLabel = formatCleaningPrice(cleaningType);
        priceState = priceLabel ? 'ready' : 'missing';
        status.textContent = priceLabel
          ? 'クリーニング料金：' + priceLabel
          : 'この品番のクリーニング料金を確認できません。品番を選び直すか、お問い合わせください。';
      } catch (error) {
        if (detailsController !== controller || controller.signal.aborted) return;
        priceState = 'error';
        status.textContent = '料金を取得できませんでした。時間をおいて品番を選び直してください。';
      } finally {
        if (detailsController === controller) {
          detailsController = null;
          syncSelection();
        }
      }
    }

    function renderProducts(items) {
      products = items;
      results.replaceChildren();

      if (!items.length) {
        closeResults();
        status.textContent = '該当する品番が見つかりませんでした。';
        return;
      }

      items.forEach((product, index) => {
        const item = document.createElement('li');
        const button = document.createElement('button');
        const copy = document.createElement('span');
        const title = document.createElement('strong');

        item.setAttribute('role', 'none');
        button.id = `${results.id}-option-${index}`;
        button.className = 'cleaning-service__result-button';
        button.type = 'button';
        button.setAttribute('role', 'option');
        button.setAttribute('aria-selected', 'false');

        if (product.featured_image && product.featured_image.url) {
          const image = document.createElement('img');
          image.className = 'cleaning-service__result-image';
          image.src = product.featured_image.url;
          image.alt = '';
          image.loading = 'lazy';
          button.appendChild(image);
        } else if (product.image) {
          const image = document.createElement('img');
          image.className = 'cleaning-service__result-image';
          image.src = product.image;
          image.alt = '';
          image.loading = 'lazy';
          button.appendChild(image);
        } else {
          const placeholder = document.createElement('span');
          placeholder.className = 'cleaning-service__result-placeholder';
          placeholder.setAttribute('aria-hidden', 'true');
          placeholder.textContent = '—';
          button.appendChild(placeholder);
        }

        copy.className = 'cleaning-service__result-copy';
        title.textContent = product.title;
        copy.appendChild(title);
        button.appendChild(copy);
        button.addEventListener('click', () => chooseProduct(product));
        item.appendChild(button);
        results.appendChild(item);
      });

      status.textContent = `${items.length}件の商品候補があります。`;
      results.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    }

    async function fetchPredictiveProducts(query, signal) {
      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'show',
        'resources[options][fields]': 'title,product_type,vendor,variants.title,variants.sku,variants.barcode'
      });
      const routesRoot = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
      const response = await fetchWithTimeout(`${routesRoot}search/suggest.json?${params.toString()}`, {
        headers: { Accept: 'application/json' },
        signal
      });
      if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
      const payload = await response.json();
      return payload.resources.results.products || [];
    }

    async function fetchGraphqlProducts(query, signal) {
      const graphqlQuery = `
        query CleaningProductSearch($query: String!) {
          search(
            first: 8
            query: $query
            types: [PRODUCT]
            prefix: LAST
            unavailableProducts: SHOW
          ) {
            nodes {
              __typename
              ... on Product {
                id
                title
                handle
                vendor
                onlineStoreUrl
                featuredImage { url }
                variants(first: 20) {
                  nodes { id title sku barcode }
                }
              }
            }
          }
        }
      `;
      const response = await fetchWithTimeout(`${window.location.origin}/api/2026-07/graphql.json`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ query: graphqlQuery, variables: { query } }),
        signal
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload || payload.errors || !payload.data || !payload.data.search) {
        const message = payload && payload.errors && payload.errors[0] && payload.errors[0].message;
        throw new Error(message || `Storefront API failed: ${response.status}`);
      }

      const routesRoot = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
      return payload.data.search.nodes
        .filter((product) => product && product.__typename === 'Product')
        .map((product) => {
          const productId = String(product.id || '').split('/').pop();
          const productUrl = product.onlineStoreUrl || `${routesRoot}products/${product.handle}`;
          const variants = product.variants && Array.isArray(product.variants.nodes)
            ? product.variants.nodes.map((variant) => {
              const variantId = String(variant.id || '').split('/').pop();
              const separator = productUrl.includes('?') ? '&' : '?';
              return {
                id: variantId,
                title: variant.title,
                sku: variant.sku,
                barcode: variant.barcode,
                url: variantId ? `${productUrl}${separator}variant=${variantId}` : productUrl
              };
            })
            : [];

          return {
            id: productId,
            title: product.title,
            vendor: product.vendor,
            url: productUrl,
            image: product.featuredImage ? product.featuredImage.url : null,
            variants
          };
        });
    }

    async function fetchStorefrontProducts(query, signal) {
      const url = new URL(searchUrl, window.location.origin);
      url.searchParams.set('view', 'cleaning-products');
      url.searchParams.set('type', 'product');
      url.searchParams.set('q', query);
      url.searchParams.set('options[prefix]', 'last');
      url.searchParams.set('options[unavailable_products]', 'show');

      const response = await fetchWithTimeout(url.toString(), {
        // The Liquid template emits JSON, but the route must render as HTML.
        headers: { Accept: 'text/html' },
        signal
      });
      if (!response.ok) throw new Error(`Storefront search failed: ${response.status}`);
      const payload = JSON.parse(await response.text());
      if (!payload || !Array.isArray(payload.products)) throw new Error('Invalid storefront search response');
      return payload.products;
    }

    async function fetchProducts(query) {
      if (unknownInput.checked) return;
      if (abortController) abortController.abort();
      const requestController = new AbortController();
      abortController = requestController;
      closeResults();
      status.textContent = '検索中…';
      searchButton.disabled = true;

      try {
        let items;
        try {
          items = await fetchGraphqlProducts(query, requestController.signal);
        } catch (graphqlError) {
          if (requestController.signal.aborted) return;
          try {
            items = await fetchStorefrontProducts(query, requestController.signal);
          } catch (storefrontError) {
            if (requestController.signal.aborted) return;
            items = await fetchPredictiveProducts(query, requestController.signal);
          }
        }
        if (abortController !== requestController || requestController.signal.aborted || unknownInput.checked) return;
        renderProducts(items);
      } catch (error) {
        if (abortController !== requestController || requestController.signal.aborted) return;
        closeResults();
        status.textContent = '商品検索に失敗しました。時間をおいて再度検索してください。';
      } finally {
        if (abortController === requestController) {
          abortController = null;
          searchButton.disabled = !manualMode || unknownInput.checked;
        }
      }
    }

    function searchCurrentValue() {
      if (unknownInput.checked) return;
      window.clearTimeout(debounceTimer);
      const query = input.value.trim();
      if (!query) {
        status.textContent = '品番または商品名を入力してください。';
        input.focus();
        return;
      }
      fetchProducts(query);
    }

    input.addEventListener('input', () => {
      clearSelection(true);
      const query = input.value.trim();
      if (query.length < 2) return;
      debounceTimer = window.setTimeout(() => fetchProducts(query), 280);
    });

    searchButton.addEventListener('click', searchCurrentValue);
    unknownInput.addEventListener('change', () => clearSelection());

    input.addEventListener('keydown', (event) => {
      if (results.hidden && (event.key === 'ArrowDown' || event.key === 'ArrowUp') && products.length) {
        results.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveResult(activeIndex + 1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveResult(activeIndex - 1);
      } else if (event.key === 'Enter' && activeIndex >= 0 && !results.hidden) {
        event.preventDefault();
        chooseProduct(products[activeIndex]);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        searchCurrentValue();
      } else if (event.key === 'Escape') {
        closeResults();
      }
    });

    clearButton.addEventListener('click', () => {
      clearSelection();
      input.focus();
    });
    document.addEventListener('click', (event) => {
      if (!search.contains(event.target)) closeResults();
    });

    clearSelection();
    return {
      isUnknown: () => unknownInput.checked,
      select: chooseProduct,
      onChange: (listener) => selectionListeners.push(listener),
      setManualMode: (manual) => {
        manualMode = manual;
        syncSelection();
      },
      validationError: () => {
        if (unknownInput.checked) return '';
        if (!selectedProduct || input.value !== selectedProduct.title) {
          return '品番検索の候補から商品を選択するか、品番不明のチェックを入れてください。';
        }
        if (priceState === 'loading') return 'クリーニング料金を確認中です。少しお待ちください。';
        if (priceState !== 'ready') return 'クリーニング料金を確認できません。商品を選び直すか、お問い合わせください。';
        return '';
      },
      reset: () => {
        unknownInput.checked = false;
        clearSelection();
      }
    };
  }

  function setupImageAttachments(root, options = {}) {
    const input = root.querySelector(options.condition ? '[data-condition-input]' : '[data-image-input]');
    const previews = root.querySelector(options.condition ? '[data-condition-previews]' : '[data-image-previews]');
    const errorOutput = root.querySelector(options.condition ? '[data-condition-error]' : '[data-file-error]');
    const maxFileSize = options.condition ? 20 * 1024 * 1024 : MAX_FILE_SIZE;
    const maxTotalSize = options.condition ? 20 * 1024 * 1024 : MAX_TOTAL_FILE_SIZE;
    const maxFiles = options.condition ? 100 : MAX_FILES;
    let files = [];
    let previewUrls = [];

    function showError(message) {
      errorOutput.textContent = message;
      errorOutput.hidden = !message;
    }

    function syncInputFiles() {
      if (typeof DataTransfer === 'undefined') return;
      const transfer = new DataTransfer();
      files.forEach((file) => transfer.items.add(file));
      input.files = transfer.files;
    }

    function renderPreviews() {
      previewUrls.forEach((url) => URL.revokeObjectURL(url));
      previewUrls = [];
      previews.replaceChildren();

      files.forEach((file, index) => {
        const figure = document.createElement('figure');
        const image = document.createElement('img');
        const remove = document.createElement('button');
        const url = URL.createObjectURL(file);

        previewUrls.push(url);
        figure.className = 'cleaning-service__preview';
        image.src = url;
        image.alt = `添付画像 ${index + 1}`;
        remove.type = 'button';
        remove.textContent = '×';
        remove.setAttribute('aria-label', `${file.name}を削除`);
        remove.addEventListener('click', () => {
          files.splice(index, 1);
          syncInputFiles();
          renderPreviews();
          showError('');
          if (options.onChange) options.onChange();
        });

        figure.append(image, remove);
        previews.appendChild(figure);
      });
    }

    input.addEventListener('change', () => {
      const incoming = Array.from(input.files || []);
      const known = new Set(files.map((file) => `${file.name}:${file.size}:${file.lastModified}`));
      const messages = [];

      incoming.forEach((file) => {
        const key = `${file.name}:${file.size}:${file.lastModified}`;
        if (known.has(key)) return;
        if (!file.size || (options.condition
          ? !/^(image\/(jpeg|png|gif|webp|avif|heic|heif))$/.test(file.type) && !/\.hei[cf]$/i.test(file.name)
          : !file.type.startsWith('image/'))) {
          messages.push(`${file.name}は画像ファイルではありません。`);
          return;
        }
        if (file.size > maxFileSize) {
          messages.push(`${file.name}は${options.condition ? 20 : 10}MBを超えています。`);
          return;
        }
        const totalSize = files.reduce((sum, currentFile) => sum + currentFile.size, 0);
        if (totalSize + file.size > maxTotalSize) {
          messages.push(`画像の合計容量は${options.condition ? 20 : 15}MB以内にしてください。`);
          return;
        }
        if (files.length >= maxFiles) {
          messages.push(`画像は最大${maxFiles}枚までです。`);
          return;
        }
        known.add(key);
        files.push(file);
      });

      syncInputFiles();
      renderPreviews();
      showError(messages.join(' '));
      if (options.onChange) options.onChange();
    });

    return {
      files: () => files.slice(),
      reset: () => {
        files = [];
        input.value = '';
        renderPreviews();
        showError('');
      }
    };
  }

  function setupConditionPhotos(root, onChange) {
    const labels = { damage: 'ほつれ・生地の傷や破れ', stain: '気になる汚れやシミ' };
    const next = root.querySelector('[data-basic-next]');
    const groups = Array.from(root.querySelectorAll('[data-condition-photos]')).map((element) => {
      const kind = element.dataset.conditionPhotos;
      const controller = setupImageAttachments(element, { condition: true, onChange: changed });
      return { kind, label: labels[kind], element, input: element.querySelector('[data-condition-input]'), enabled: false, ...controller };
    });
    function missing() { return groups.find(group => group.enabled && !group.files().length)?.input || null; }
    function changed() {
      if (next) next.disabled = Boolean(missing());
      if (onChange) onChange();
    }
    function sync() {
      groups.forEach((group) => {
        const enabled = Array.from(root.querySelectorAll('[data-condition-choice]'))
          .some(option => option.dataset.conditionChoice === group.kind && option.checked && option.value === 'あり');
        if (!enabled && group.enabled) group.reset();
        group.enabled = enabled;
        group.element.hidden = !enabled;
        group.input.disabled = !enabled;
        group.input.required = enabled;
        group.input.setAttribute('aria-required', String(enabled));
      });
      changed();
    }
    sync();
    return {
      sync, missing, groups: () => groups,
      reset() { groups.forEach(group => group.reset()); sync(); },
      async prepareSubmission(data, endpoint) {
        if (!groups.length) return; // Compatibility with an older section during theme updates.
        if (missing()) throw new Error('「あり」を選択した項目の写真を添付してください。');
        data.set('cleaning_photos_version', '1');
        const session = crypto.randomUUID();
        const references = [];
        const uploadUrl = new URL(endpoint, window.location.origin);
        uploadUrl.pathname = '/cleaning-photos';
        uploadUrl.search = '';
        for (const group of groups) {
          data.set(`contact[${group.label}]`, group.enabled ? 'あり' : 'なし');
          if (!group.enabled) continue;
          for (const file of group.files()) {
            const upload = new FormData();
            upload.set('photo', file, file.name);
            upload.set('kind', group.kind);
            upload.set('session', session);
            upload.set('email', String(data.get('contact[email]') || ''));
            upload.set('website', String(data.get('contact[website]') || ''));
            const response = await fetchWithTimeout(uploadUrl.toString(), { method: 'POST', headers: { Accept: 'application/json' }, body: upload }, 120000);
            const result = await response.json().catch(() => null);
            if (!response.ok || !result || result.success !== true || typeof result.id !== 'string') {
              throw new Error(result && result.message ? result.message : '確認写真を保存できませんでした。もう一度お試しください。');
            }
            references.push(result.id);
          }
        }
        data.set('cleaning_photo_session', session);
        data.set('cleaning_photo_ids', JSON.stringify(references));
      }
    };
  }

  function setupTermsModal(root, onChange) {
    const modal = root.querySelector('[data-terms-modal]');
    const trigger = root.querySelector('[data-terms-open]');
    const previewTrigger = root.querySelector('[data-terms-preview]');
    const previewClose = root.querySelector('[data-terms-preview-close]');
    const choiceGroup = root.querySelector('[data-terms-choices]');
    const landing = root.querySelector('[data-overview-landing]');
    const acknowledgement = root.querySelector('[data-confirmation-acknowledgement]');
    const label = root.querySelector('[data-terms-label]');
    const form = root.querySelector('form');
    const content = root.querySelector('.cleaning-service__inner');
    let agreed = false;
    let isOpen = false;
    let contentWasInert = false;
    let landingWasInert = false;
    let isPreview = false;
    let activeTrigger = trigger;
    let closeTimer;
    // Fail closed if an outdated Liquid template is used with this asset.
    if (acknowledgement) acknowledgement.checked = false;
    if (!modal || !trigger || !acknowledgement || !label) {
      return { isAgreed: () => false, reset() {} };
    }
    const dialog = modal.querySelector('[data-terms-dialog]');
    const choices = Array.from(modal.querySelectorAll('[data-terms-decision]'));

    function setAgreed(value, notify = true) {
      agreed = value;
      acknowledgement.checked = value;
      label.textContent = value ? '注意事項に同意しました。' : 'お申し込み前の注意事項';
      trigger.dataset.agreed = String(value);
      if (notify) onChange();
    }

    function closeModal() {
      if (!isOpen) return;
      isOpen = false;
      modal.classList.remove('is-open');
      document.body.classList.remove('cleaning-service-terms-modal-open');
      if (content) content.inert = contentWasInert;
      if (landing) landing.inert = landingWasInert;
      activeTrigger.setAttribute('aria-expanded', 'false');
      activeTrigger.focus({ preventScroll: true });
      modal.inert = true;
      modal.setAttribute('aria-hidden', 'true');
      closeTimer = window.setTimeout(() => { modal.hidden = true; }, 440);
    }

    function reset() {
      closeModal();
      choices.forEach(choice => { choice.checked = false; });
      setAgreed(false);
    }

    function openModal(preview = false) {
      const opener = preview ? previewTrigger : trigger;
      if (isOpen || !opener || opener.disabled) return;
      isPreview = preview;
      activeTrigger = opener;
      // Reading from STEP 01 never grants consent or changes the final confirmation.
      if (!preview) {
        setAgreed(false);
        choices.forEach(choice => { choice.checked = false; });
      }
      if (choiceGroup) choiceGroup.hidden = preview;
      if (previewClose) previewClose.hidden = !preview;
      isOpen = true;
      window.clearTimeout(closeTimer);
      modal.hidden = false;
      modal.inert = false;
      modal.setAttribute('aria-hidden', 'false');
      activeTrigger.setAttribute('aria-expanded', 'true');
      if (content) { contentWasInert = content.inert; content.inert = true; }
      if (landing) { landingWasInert = landing.inert; landing.inert = true; }
      document.body.classList.add('cleaning-service-terms-modal-open');
      dialog.scrollTop = 0;
      window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
        if (!isOpen) return;
        modal.classList.add('is-open');
        dialog.focus({ preventScroll: true });
      }));
    }
    trigger.addEventListener('click', () => openModal());
    if (previewTrigger && previewClose && choiceGroup) {
      previewTrigger.addEventListener('click', () => openModal(true));
      previewClose.addEventListener('click', closeModal);
    }
    choices.forEach(choice => choice.addEventListener('change', () => {
      if (!isOpen || isPreview || !choice.checked) return;
      setAgreed(choice.value === 'agree');
      closeModal();
    }));
    modal.addEventListener('keydown', event => {
      if (!isOpen) return;
      if (event.key === 'Escape') { event.preventDefault(); if (isPreview) closeModal(); else reset(); return; }
      // Include the scrollable dialog in the focus cycle; do not skip the long text on open.
      if (event.key !== 'Tab') return;
      event.preventDefault();
      const targets = isPreview ? [dialog, previewClose] : [dialog, ...choices];
      const index = targets.indexOf(document.activeElement);
      targets[(index + (event.shiftKey ? -1 : 1) + targets.length) % targets.length].focus();
    });
    form.addEventListener('reset', reset);
    setAgreed(false, false);
    return { isAgreed: () => agreed && acknowledgement.checked, reset };
  }


  function setupEntryModal(root) {
    const modal = root.querySelector('[data-entry-modal]');
    if (!modal) return;

    const dialog = modal.querySelector('[role="dialog"]');
    const primaryAction = modal.querySelector('.cleaning-service-modal__button--primary');
    const closeButtons = modal.querySelectorAll('[data-modal-close]');
    let previouslyFocused = null;
    let closeTimer;

    function focusableElements() {
      return Array.from(dialog.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'))
        .filter((element) => !element.hidden);
    }

    function openModal() {
      previouslyFocused = document.activeElement;
      window.clearTimeout(closeTimer);
      modal.hidden = false;
      document.body.classList.add('cleaning-service-modal-open');

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          modal.classList.add('is-open');
          if (primaryAction) primaryAction.focus({ preventScroll: true });
        });
      });
    }

    function closeModal() {
      modal.classList.remove('is-open');
      document.body.classList.remove('cleaning-service-modal-open');
      closeTimer = window.setTimeout(() => {
        modal.hidden = true;
      }, 440);

      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus({ preventScroll: true });
      }
    }

    closeButtons.forEach((button) => button.addEventListener('click', closeModal));

    modal.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const focusable = focusableElements();
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    window.setTimeout(openModal, 260);
  }

  function initializeAll(scope = document) {
    if (scope.matches && scope.matches(ROOT_SELECTOR)) initializeCleaningForm(scope);
    scope.querySelectorAll(ROOT_SELECTOR).forEach(initializeCleaningForm);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
